import React, { useEffect, useState } from 'react';
import { db } from '../../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { SuccessStoryModel, TERM_TYPES, SUBJECT_TYPES } from '../../../models';
import { GraduationCap, Search, X, Filter, RotateCcw, BookOpen, Calendar, Award } from 'lucide-react';
import { LaurelSuccessCard } from '../../../components/ui/LaurelSuccessCard';

const IntroSuccessStories: React.FC = () => {
    const [stories, setStories] = useState<SuccessStoryModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSubTab, setActiveSubTab] = useState<'grade' | 'improvement' | 'rank' | 'standing' | 'high_school' | 'university'>('grade');
    const [selectedYear, setSelectedYear] = useState<string>('전체');
    const [selectedSemester, setSelectedSemester] = useState<string>('전체');
    const [selectedSubject, setSelectedSubject] = useState<string>('전체');

    // Infinite Scroll
    const [visibleCount, setVisibleCount] = useState(6);
    const observerTargetRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchStories = async () => {
            try {
                const q = query(collection(db, 'successStories'), orderBy('createdAt', 'desc'));
                const querySnapshot = await getDocs(q);
                const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SuccessStoryModel));
                setStories(data);
            } catch (error) {
                console.error('Error fetching success stories:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStories();
    }, []);

    // Available years extracted dynamically
    const availableYears = Array.from(
        new Set(
            stories
                .map(s => s.year)
                .filter((y): y is string => Boolean(y && y.trim()))
        )
    ).sort((a, b) => Number(b) - Number(a));

    const currentYear = new Date().getFullYear();
    const yearList = availableYears.length > 0
        ? availableYears
        : [currentYear.toString(), (currentYear - 1).toString(), (currentYear - 2).toString()];

    const isYearMatch = (yearInDb?: string, selectedYear?: string) => {
        if (!selectedYear || selectedYear === '전체') return true;
        if (!yearInDb) return false;
        const dbY = yearInDb.toString().replace(/년|\s+/g, '');
        const selY = selectedYear.toString().replace(/년|\s+/g, '');
        return dbY === selY;
    };

    const isSemesterMatch = (semesterInDb?: string, selectedSemester?: string) => {
        if (!selectedSemester || selectedSemester === '전체') return true;
        if (!semesterInDb) return false;
        const dbS = semesterInDb.toString().replace(/고사|\s+/g, '');
        const selS = selectedSemester.toString().replace(/고사|\s+/g, '');
        return dbS === selS || dbS.includes(selS) || selS.includes(dbS);
    };

    const isSubjectMatch = (subjectInDb?: string, selectedSubject?: string) => {
        if (!selectedSubject || selectedSubject === '전체') return true;
        if (!subjectInDb) return false;
        return subjectInDb.trim() === selectedSubject.trim();
    };

    // Filter & Sort stories by highest rank/score first
    const filteredStories = stories
        .filter(s => {
            const matchesTab = (s.type || 'university') === activeSubTab;
            const matchesYear = isYearMatch(s.year, selectedYear);
            const matchesSemester = isSemesterMatch(s.semester, selectedSemester);
            const matchesSubject = isSubjectMatch(s.subject, selectedSubject);
            const matchesSearch =
                (s.studentName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (s.school || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (s.admissionSchool || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (s.subject || '').toLowerCase().includes(searchTerm.toLowerCase());
            return matchesTab && matchesYear && matchesSemester && matchesSubject && matchesSearch;
        })
        .sort((a, b) => {
            const type = a.type || 'university';

            if (type === 'grade') {
                // High score first (descending: 100 > 95 > 90)
                const scoreA = parseFloat((a.score || '').replace(/[^0-9.]/g, '')) || 0;
                const scoreB = parseFloat((b.score || '').replace(/[^0-9.]/g, '')) || 0;
                if (scoreB !== scoreA) return scoreB - scoreA;
            } else if (type === 'rank') {
                // Small rank number first (ascending: 1등급 < 2등급 < 3등급)
                const rankA = parseFloat((a.rank || '').replace(/[^0-9.]/g, '')) || 99;
                const rankB = parseFloat((b.rank || '').replace(/[^0-9.]/g, '')) || 99;
                if (rankA !== rankB) return rankA - rankB;
            } else if (type === 'standing') {
                // Small standing number first (ascending: 1등 < 2등 < 3등)
                const standA = parseFloat((a.standing || '').replace(/[^0-9.]/g, '')) || 999;
                const standB = parseFloat((b.standing || '').replace(/[^0-9.]/g, '')) || 999;
                if (standA !== standB) return standA - standB;
            } else if (type === 'improvement') {
                // High improvement first (descending: 30점 향상 > 20점 향상)
                const impA = parseFloat((a.improvementScore || '').replace(/[^0-9.]/g, '')) || 0;
                const impB = parseFloat((b.improvementScore || '').replace(/[^0-9.]/g, '')) || 0;
                if (impB !== impA) return impB - impA;
            }

            // Secondary sort: newest createdAt first
            return (b.createdAt || 0) - (a.createdAt || 0);
        });

    const displayedStories = filteredStories.slice(0, visibleCount);
    const hasMore = visibleCount < filteredStories.length;

    useEffect(() => {
        setVisibleCount(6);
    }, [searchTerm, activeSubTab, selectedYear, selectedSemester, selectedSubject]);

    useEffect(() => {
        const target = observerTargetRef.current;
        if (!target || !hasMore) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setVisibleCount(prev => prev + 6);
            }
        }, { threshold: 0.1, rootMargin: '100px' });

        observer.observe(target);
        return () => {
            if (target) observer.unobserve(target);
        };
    }, [hasMore, filteredStories]);

    const resetFilters = () => {
        setSelectedYear('전체');
        setSelectedSemester('전체');
        setSelectedSubject('전체');
        setSearchTerm('');
    };

    const isFilterActive = selectedYear !== '전체' || selectedSemester !== '전체' || selectedSubject !== '전체' || searchTerm !== '';

    return (
        <div className="animate-in fade-in duration-700 bg-slate-950 text-white min-h-screen">
            {/* Hero Section */}
            <section className="py-16 md:py-20 px-4 md:px-8 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-950 text-white relative overflow-hidden">
                <div className="max-w-5xl mx-auto text-center space-y-6 relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 text-indigo-300 font-black text-[10px] uppercase tracking-wider border border-indigo-500/20 shadow-inner">
                        <Award size={14} className="text-indigo-400" />
                        Hall of Fame
                    </div>
                    <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-tight break-keep">
                        땀방울로 증명한 눈부신 결실, <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-violet-300 to-indigo-300">정성우학원 명예의 전당</span>
                    </h1>
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-400 font-medium max-w-2xl mx-auto break-keep">
                        우수한 성적 성취와 놀라운 향상, 그리고 빛나는 합격의 순간까지 <br />
                        정성우학원과 함께 스스로 증명해낸 영광스러운 기록들을 만나보세요.
                    </p>
                </div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/15 blur-[120px] rounded-full pointer-events-none"></div>
            </section>

            {/* Stories List & Control Panel */}
            <section className="py-12 md:py-20 px-4 md:px-8 max-w-5xl mx-auto">

                {/* Main Unified Control Panel */}
                <div className="bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-3xl p-4 sm:p-6 shadow-2xl space-y-5 max-w-4xl mx-auto mb-12">
                    
                    {/* Category SubTabs */}
                    <div className="flex flex-wrap sm:flex-nowrap gap-1.5 p-1.5 bg-slate-950/80 rounded-2xl border border-white/5">
                        <button
                            onClick={() => setActiveSubTab('grade')}
                            className={`flex-1 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all ${activeSubTab === 'grade'
                                ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25'
                                : 'text-slate-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            성적
                        </button>
                        <button
                            onClick={() => setActiveSubTab('improvement')}
                            className={`flex-1 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all ${activeSubTab === 'improvement'
                                ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25'
                                : 'text-slate-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            향상
                        </button>
                        <button
                            onClick={() => setActiveSubTab('rank')}
                            className={`flex-1 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all ${activeSubTab === 'rank'
                                ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25'
                                : 'text-slate-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            등급
                        </button>
                        <button
                            onClick={() => setActiveSubTab('standing')}
                            className={`flex-1 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all ${activeSubTab === 'standing'
                                ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25'
                                : 'text-slate-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            등수
                        </button>
                        <button
                            onClick={() => setActiveSubTab('high_school')}
                            className={`flex-1 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all ${activeSubTab === 'high_school'
                                ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25'
                                : 'text-slate-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            고입
                        </button>
                        <button
                            onClick={() => setActiveSubTab('university')}
                            className={`flex-1 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all ${activeSubTab === 'university'
                                ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25'
                                : 'text-slate-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            대입
                        </button>
                    </div>

                    {/* Filter Bar (Subject, Year, Semester, Search) */}
                    <div className="flex flex-col md:flex-row items-center justify-between gap-3.5 pt-1">
                        
                        {/* Dropdown Filters */}
                        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                            <div className="hidden sm:flex items-center gap-1.5 text-xs font-bold text-slate-400 mr-1">
                                <Filter size={14} className="text-indigo-400" />
                                <span>필터</span>
                            </div>

                            {/* 과목 필터 */}
                            <select
                                value={selectedSubject}
                                onChange={(e) => setSelectedSubject(e.target.value)}
                                className="px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950/90 text-slate-200 text-xs font-bold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none cursor-pointer hover:border-slate-700 transition-all"
                            >
                                <option value="전체">전체 과목</option>
                                {SUBJECT_TYPES.map(sub => (
                                    <option key={sub} value={sub}>{sub}</option>
                                ))}
                            </select>

                            {/* 연도 필터 */}
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                                className="px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950/90 text-slate-200 text-xs font-bold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none cursor-pointer hover:border-slate-700 transition-all"
                            >
                                <option value="전체">전체 연도</option>
                                {yearList.map(yr => (
                                    <option key={yr} value={yr}>{yr}년</option>
                                ))}
                            </select>

                            {/* 학기/시험 필터 */}
                            <select
                                value={selectedSemester}
                                onChange={(e) => setSelectedSemester(e.target.value)}
                                className="px-3.5 py-2 rounded-xl border border-slate-800 bg-slate-950/90 text-slate-200 text-xs font-bold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none cursor-pointer hover:border-slate-700 transition-all"
                            >
                                <option value="전체">전체 학기/시험</option>
                                {TERM_TYPES.map(term => (
                                    <option key={term} value={term}>{term}</option>
                                ))}
                            </select>

                            {isFilterActive && (
                                <button
                                    onClick={resetFilters}
                                    className="flex items-center gap-1 text-xs font-bold text-indigo-400 hover:text-indigo-300 px-2 py-1 transition-colors"
                                >
                                    <RotateCcw size={12} />
                                    <span>초기화</span>
                                </button>
                            )}
                        </div>

                        {/* Search Input */}
                        <div className="relative w-full md:w-64">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" size={15} />
                            <input
                                type="text"
                                placeholder="이름, 학교, 과목 검색..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-8 py-2 rounded-xl border border-slate-800 bg-slate-950/90 text-slate-100 text-xs outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all placeholder:text-slate-500 font-medium"
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Section Title */}
                <div className="text-center md:text-left mb-10">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-white flex items-center justify-center md:justify-start gap-2">
                        <span>
                            {activeSubTab === 'university'
                                ? '영광의 합격자 명단 (대학)'
                                : activeSubTab === 'high_school'
                                    ? '영광의 합격자 명단 (고등학교)'
                                    : activeSubTab === 'grade'
                                        ? '명예의 전당 (성적 우수자)'
                                        : activeSubTab === 'rank'
                                            ? '명예의 전당 (등급 우수자)'
                                            : activeSubTab === 'standing'
                                                ? '명예의 전당 (등수 우수자)'
                                                : '명예의 전당 (성적 향상자)'}
                        </span>
                        <span className="text-xs font-bold text-indigo-400 bg-indigo-500/10 px-2.5 py-1 rounded-full border border-indigo-500/20">
                            {filteredStories.length}건
                        </span>
                    </h2>
                </div>

                {/* Card List */}
                {loading ? (
                    <div className="space-y-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-40 rounded-[2rem] bg-slate-900/60 border border-white/5 animate-pulse"></div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {displayedStories.map((story) => (
                            <div key={story.id} className="group">
                                <LaurelSuccessCard story={story} />
                            </div>
                        ))}
                    </div>
                )}

                {/* Infinite Scroll Trigger */}
                {hasMore && (
                    <div ref={observerTargetRef} className="flex justify-center items-center py-10 mt-6">
                        <div className="flex gap-2 items-center text-slate-400 text-xs font-black">
                            <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            <span className="ml-2">더 많은 실적 소식 불러오는 중...</span>
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {filteredStories.length === 0 && !loading && (
                    <div className="text-center py-24 bg-slate-900/60 rounded-[3rem] border border-white/10 backdrop-blur">
                        <GraduationCap className="mx-auto text-indigo-400/50 mb-4 animate-pulse" size={60} />
                        <p className="text-slate-300 font-bold text-lg mb-2">
                            조건에 맞는 실적이 없습니다.
                        </p>
                        <p className="text-slate-500 text-xs font-medium">
                            선택하신 필터나 검색 조건을 변경하여 다시 확인해보세요.
                        </p>
                        {isFilterActive && (
                            <button
                                onClick={resetFilters}
                                className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-500 transition-all shadow-lg shadow-indigo-600/20"
                            >
                                <RotateCcw size={14} />
                                필터 전체 초기화
                            </button>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
};

export default IntroSuccessStories;
